import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useBookersContent } from '../hooks/useBookersContent';
import LanguageSwitcher from './LanguageSwitcher';
import { HeaderLogo } from './BookersLogo';
import { ADMIN_LOGIN_URL } from '../lib/links';

export default function Navbar() {
  const { t } = useI18n();
  const { navFeatures } = useBookersContent();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const closeTimer = useRef(null);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(false), 160);
  };
  const closeNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass = 'text-[15px] font-medium text-[#3a3a42] hover:text-[#0b0b0f] transition-colors';

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/85 backdrop-blur-xl border-b border-black/5' : 'bg-white/60 backdrop-blur-md'
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-[68px] items-center justify-between">
          <HeaderLogo />

          <div className="hidden lg:flex items-center gap-8" onMouseEnter={openNow} onMouseLeave={closeSoon}>
            <div className="relative" onMouseEnter={openNow}>
              <a href="/#features" className={`flex items-center gap-1 ${linkClass}`}>
                {t('nav.features')} <ChevronDown size={15} className={`transition-transform ${openMenu ? 'rotate-180' : ''}`} />
              </a>
            </div>
            <Link to="/solutions" className={linkClass} onMouseEnter={closeNow}>{t('nav.solutions')}</Link>
            <Link to="/pricing" className={linkClass} onMouseEnter={closeNow}>{t('nav.pricing')}</Link>
            <Link to="/contact" className={linkClass} onMouseEnter={closeNow}>{t('nav.contact')}</Link>

            <div
              className={`absolute left-0 right-0 top-[56px] pt-3 transition-all duration-200 ${
                openMenu ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
              }`}
              onMouseEnter={openNow}
              onMouseLeave={closeSoon}
            >
              <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <div className="bg-white rounded-3xl border border-black/5 shadow-[0_30px_80px_rgba(15,15,20,0.14)] p-7 grid grid-cols-3 gap-8">
                  {navFeatures.map((col) => (
                    <div key={col.group}>
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#8b8b95] mb-4">{col.group}</p>
                      <ul className="space-y-3">
                        {col.items.map((it) => (
                          <li key={it.title}>
                            <a href="/#features" className="group flex items-start gap-3 rounded-xl p-2 -m-2 hover:bg-brand-muted-hover transition-colors">
                              <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-brand-muted flex items-center justify-center group-hover:bg-white">
                                <it.icon size={18} className="text-brand-dark" />
                              </span>
                              <span>
                                <span className="block text-sm font-semibold text-[#0b0b0f]">{it.title}</span>
                                <span className="block text-[13px] text-[#6b6b73] leading-snug">{it.desc}</span>
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <a href={ADMIN_LOGIN_URL} className={linkClass}>{t('nav.login')}</a>
            <Link to="/signup" className="btn-pill btn-brand px-5 py-2.5 text-sm">{t('nav.signUp')}</Link>
          </div>

          <button className="lg:hidden p-2 text-[#0b0b0f]" onClick={() => setMobileOpen(true)} aria-label={t('nav.openMenu')}>
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-white">
          <div className="flex h-[68px] items-center justify-between px-5 border-b border-black/5">
            <HeaderLogo />
            <button className="p-2" onClick={() => setMobileOpen(false)} aria-label={t('nav.closeMenu')}><X size={24} /></button>
          </div>
          <div className="px-5 py-6 flex flex-col gap-1">
            <a href="/#features" onClick={() => setMobileOpen(false)} className="py-3 text-lg font-medium text-[#0b0b0f] border-b border-black/5">{t('nav.features')}</a>
            <Link to="/solutions" onClick={() => setMobileOpen(false)} className="py-3 text-lg font-medium text-[#0b0b0f] border-b border-black/5">{t('nav.solutions')}</Link>
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="py-3 text-lg font-medium text-[#0b0b0f] border-b border-black/5">{t('nav.pricing')}</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-3 text-lg font-medium text-[#0b0b0f] border-b border-black/5">{t('nav.contact')}</Link>
            <div className="mt-4 mb-2">
              <LanguageSwitcher />
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <a href={ADMIN_LOGIN_URL} className="btn-pill btn-light py-3">{t('nav.login')}</a>
              <Link to="/signup" onClick={() => setMobileOpen(false)} className="btn-pill btn-brand py-3">{t('nav.signUp')}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
